/**
 * UnassignedEnquiriesTab — the HQ review pool for membership enquiries that
 * arrived without naming a church. Posting one to a church moves it into that
 * church's Applications tab, where it is approved or rejected as usual.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Inbox, Mail, Phone, Clock, Church } from 'lucide-react';
import { toast } from 'sonner';
import { membershipEnquiryAPI, churchesAPI } from '@/lib/api';

const STATUS_COLORS = {
  pending: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
  approved: 'bg-emerald-500/20 text-emerald-300 border-emerald-400/30',
  rejected: 'bg-rose-500/20 text-rose-300 border-rose-400/30',
};

const EnquiryCard = ({ row, churches, onAssign, assigning }) => {
  const [churchId, setChurchId] = useState('');
  return (
    <Card className="bg-white/5 backdrop-blur-xl border-white/10 text-white"
          data-testid={`enquiry-card-${row.id}`}>
      <CardContent className="pt-5 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 className="font-bold text-lg" data-testid={`enquiry-name-${row.id}`}>
              {row.full_name || 'Unnamed enquirer'}
            </h4>
            <p className="text-xs text-slate-400 capitalize">
              Heard about us: {(row.heard_about || 'other').replace(/_/g, ' ')}
            </p>
          </div>
          <Badge variant="outline" className={`capitalize ${STATUS_COLORS[row.status] || ''}`}>
            {row.status}
          </Badge>
        </div>

        <div className="space-y-1 text-sm text-slate-300">
          {row.email && <p className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /> {row.email}</p>}
          {row.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {row.phone}</p>}
          {row.created_at && (
            <p className="flex items-center gap-2 text-xs text-slate-400">
              <Clock className="h-3.5 w-3.5" /> {new Date(row.created_at).toLocaleString()}
            </p>
          )}
          {row.family_notes && <p className="text-xs text-slate-400">{row.family_notes}</p>}
          {row.prayer_request && (
            <p className="text-xs italic text-slate-400">“{row.prayer_request}”</p>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <Select value={churchId} onValueChange={setChurchId}>
            <SelectTrigger className="bg-white/5 border-white/20 text-white"
                           data-testid={`enquiry-church-select-${row.id}`}>
              <SelectValue placeholder="Post to church…" />
            </SelectTrigger>
            <SelectContent>
              {churches.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" disabled={!churchId || assigning}
                  onClick={() => onAssign(row.id, churchId)}
                  className="bg-indigo-500 hover:bg-indigo-400 text-white shrink-0"
                  data-testid={`enquiry-assign-btn-${row.id}`}>
            <Church className="h-4 w-4 mr-1" /> Assign
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const UnassignedEnquiriesTab = () => {
  const qc = useQueryClient();
  const [status, setStatus] = useState('pending');

  const { data, isLoading } = useQuery({
    queryKey: ['hq-membership-enquiries', status],
    queryFn: () => membershipEnquiryAPI.listUnassigned(status).then((r) => r.data),
  });
  const { data: churches = [] } = useQuery({
    queryKey: ['hq-churches-for-enquiries'],
    queryFn: () => churchesAPI.list().then((r) => (Array.isArray(r.data) ? r.data : r.data?.items || [])),
  });

  const assignMut = useMutation({
    mutationFn: ({ id, churchId }) => membershipEnquiryAPI.assign(id, churchId),
    onSuccess: (r) => {
      toast.success(`Posted to ${r.data?.church_name || 'the church'} — review it on their Applications tab`);
      qc.invalidateQueries({ queryKey: ['hq-membership-enquiries'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not assign the enquiry'),
  });

  const rows = data?.items || [];
  const counts = data?.counts || {};

  return (
    <div className="space-y-4" data-testid="hq-unassigned-enquiries-tab">
      <div className="flex items-center gap-2 flex-wrap">
        {['pending', 'approved', 'rejected', 'all'].map((s) => (
          <Button key={s} size="sm" variant={status === s ? 'default' : 'outline'}
                  onClick={() => setStatus(s)}
                  className={status === s
                    ? 'bg-indigo-500 hover:bg-indigo-400 text-white'
                    : 'text-white border-white/20 bg-white/5 hover:bg-white/10'}
                  data-testid={`enquiries-filter-${s}`}>
            <span className="capitalize">{s}</span>
            {counts[s] > 0 && <span className="ml-2 text-xs opacity-80">{counts[s]}</span>}
          </Button>
        ))}
      </div>

      <p className="text-xs text-slate-400">
        Enquiries from the public “request to join” form that named no church.
        Post one to a church and it joins their Applications queue.
      </p>

      <div className="grid md:grid-cols-2 gap-4">
        {isLoading && <p className="text-slate-500 text-sm">Loading…</p>}
        {!isLoading && rows.length === 0 && (
          <div className="col-span-2 border border-white/10 rounded-xl py-14 text-center"
               data-testid="enquiries-empty">
            <Inbox className="h-7 w-7 mx-auto text-slate-500" />
            <p className="mt-4 text-sm text-slate-400">No unassigned enquiries waiting.</p>
          </div>
        )}
        {rows.map((row) => (
          <EnquiryCard key={row.id} row={row} churches={churches}
                       assigning={assignMut.isPending}
                       onAssign={(id, churchId) => assignMut.mutate({ id, churchId })} />
        ))}
      </div>
    </div>
  );
};

export default UnassignedEnquiriesTab;
